import React, { Component } from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import { logout } from '../../utils';
import './AccountTheme.css';
import AccountConfirmationDialog from './AccountConfirmationDialog';

function MenuIcon({type}) {
    const paths = {
        profile: <><circle cx="12" cy="8" r="3.5"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></>,
        address: <><path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z"/><circle cx="12" cy="10" r="2.5"/></>,
        orders: <><path d="m3 7 9-4 9 4v10l-9 4-9-4Z"/><path d="m3 7 9 4 9-4M12 11v10M7.5 5l9 4v5"/></>,
        wishlist: <path d="M20.5 5.5a5 5 0 0 0-7 0L12 7l-1.5-1.5a5 5 0 0 0-7 7L12 21l8.5-8.5a5 5 0 0 0 0-7Z"/>,
        reviews: <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9Z"/>,
        logout: <><path d="M10 4H5v16h5M10 12h11m-4-4 4 4-4 4"/></>,
    };
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[type]}</svg>;
}

class AccountMenu extends Component {

    constructor(props) {
        super(props);
        this.state = {confirmLogout:false, loggingOut:false, menuOpen:false};
        this.menuToggle = React.createRef();
    }

    componentDidUpdate(previousProps) {
        if (previousProps.location.pathname !== this.props.location.pathname && this.state.menuOpen) {
            this.setState({menuOpen:false});
        }
    }

    async handleLogout(){
        if (!this.state.confirmLogout || this.state.loggingOut) return;
        this.setState({loggingOut:true});
        try {
            await logout();
        } finally {
            this.setState({loggingOut:false, confirmLogout:false});
        }
    }

    render() {
        const profileName = this.props.profileName || localStorage.getItem('natureway_username');
        const profileInitials = profileName
            ? profileName.split(/\s+/).slice(0, 2).map((part)=>part.charAt(0)).join('').toUpperCase()
            : 'NA';
        const pathname = this.props.location.pathname;
        const currentSection = [
            ['/address', 'Manage Addresses', 'address'], ['/orders', 'Orders', 'orders'],
            ['/wishlist', 'My Wishlist', 'wishlist'], ['/reviews', 'My Reviews & Ratings', 'reviews'],
        ].find(([path]) => pathname === path || pathname.startsWith(`${path}/`)) || ['/account', 'Profile Information', 'profile'];
        return (
            <><aside className="account-side account-side--natureway" onKeyDown={event => {
                if (event.key === 'Escape' && this.state.menuOpen && !this.state.confirmLogout) {
                    this.setState({menuOpen:false}, () => this.menuToggle.current?.focus());
                }
            }}>
                <button type="button" className="account-side__mobile-toggle" ref={this.menuToggle}
                    aria-label="Account menu" aria-expanded={this.state.menuOpen} aria-controls="account-navigation"
                    onClick={() => this.setState(previous => ({menuOpen:!previous.menuOpen}))}>
                    <MenuIcon type={currentSection[2]}/>
                    <span className="account-side__mobile-current"><small>My account</small><strong>{currentSection[1]}</strong></span>
                    <span className="account-side__mobile-action">{this.state.menuOpen?'Close':'Menu'}</span>
                    <svg className="account-side__mobile-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
                </button>
                    <div className="account-side__profile">
                        <div className="account-side__avatar">{profileInitials}</div>
                        <div className="account-side__profileCopy">
                            <span>Welcome to Nature Way</span>
                            <strong>{profileName || 'Profile'}</strong>
                        </div>
                    </div>

                <nav id="account-navigation" className={`account-side__nav${this.state.menuOpen?' account-side__nav--open':''}`} aria-label="Account navigation"
                    onClick={event => {if (event.target.closest('a')) this.setState({menuOpen:false});}}>
                    <div className="account-side__group">
                        <span className="account-side__groupLabel">Account</span>
                        <NavLink
                            exact
                            to="/account"
                            className="account-side__link"
                            activeClassName="account-side__link--active"
                        >
                            <MenuIcon type="profile"/>
                            <span>Profile Information</span>
                        </NavLink>
                        <NavLink
                            to="/address"
                            className="account-side__link"
                            activeClassName="account-side__link--active"
                        >
                            <MenuIcon type="address"/>
                            <span>Manage Addresses</span>
                        </NavLink>
                    </div>

                    <div className="account-side__group">
                        <span className="account-side__groupLabel">Orders</span>
                        <NavLink
                            to="/orders"
                            className="account-side__link"
                            activeClassName="account-side__link--active"
                        >
                            <MenuIcon type="orders"/>
                            <span>Orders</span>
                        </NavLink>
                        <NavLink
                            to="/wishlist"
                            className="account-side__link"
                            activeClassName="account-side__link--active"
                        >
                            <MenuIcon type="wishlist"/>
                            <span>My Wishlist</span>
                        </NavLink>
                        <NavLink
                            to="/reviews"
                            className="account-side__link"
                            activeClassName="account-side__link--active"
                        >
                            <MenuIcon type="reviews"/>
                            <span>My Reviews & Ratings</span>
                        </NavLink>
                    </div>

                    <button
                        type="button"
                        className="account-side__logout"
                        onClick={() => this.setState({confirmLogout:true})}
                    >
                        <span className="account-side__logoutIcon"><MenuIcon type="logout"/></span>
                        <span>Sign out</span>
                        <span className="account-side__logoutArrow" aria-hidden="true">→</span>
                    </button>
                </nav>
            </aside>
            {this.state.confirmLogout && <AccountConfirmationDialog
                title="Sign out of your account?"
                description="You’ll need to sign in again to access your account, orders and saved favourites."
                cancelLabel="Stay signed in" confirmLabel="Sign out" busyLabel="Signing out…"
                busy={this.state.loggingOut}
                onCancel={() => this.setState({confirmLogout:false})}
                onConfirm={() => this.handleLogout()}
            />}</>
        );
    }
}

export default withRouter(AccountMenu);
